import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { SESSION_STORAGE_VARIABLES, sessionStorageManager } from '@app/services/storageManager';
import { supportedLocales } from '@app/services/translations';
import { ROUTE_LABELS, ROUTES } from '@app/router/constants';
import { useBuildNavigationTabs, useNavigate, useScreenResolution } from '@app/hooks';
import { selectLocale } from '@app/redux';

import CommonTabs from './CommonTabs';
import HamburgerMenuButton from './HamburgerMenuButton';
import LanguageToggle from './LanguageToggle';
import MobileBottomNav from './MobileBottomNav';
import ProfileButton from './ProfileButton';
import { TabItem } from './TabItem';
import ViasatLogo from './ViasatLogo';
import { NavItem } from './types';
import { TAB_CONFIG } from './constants';

import styles from './styles.module.scss';

const HeaderNavigation = (): JSX.Element => {
  const {
    locale: { urlPrefix, userLocale },
  } = useSelector(selectLocale);

  const { isLarge, isExtraLarge } = useScreenResolution();
  const [isOpen, setIsOpen] = useState(false);

  const { goTo } = useNavigate();

  const useMobileLayout = !isLarge && !isExtraLarge;
  const testId = useMobileLayout ? 'mobileNav' : 'desktopNav';
  const { t } = useTranslation(['Nav', 'Global']);

  const tabItems = useBuildNavigationTabs(TAB_CONFIG);

  const open = (): void => {
    window.scrollTo(0, 0);
    setIsOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const close = (): void => {
    window.scrollTo(0, 0);
    setIsOpen(false);
    document.body.style.overflow = '';
  };

  const createNavItemClickHandler =
    (path: string): ((e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void) =>
    (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      e.preventDefault();
      close();

      goTo(path);
      sessionStorageManager.removeItem(SESSION_STORAGE_VARIABLES.SHOW_PLAN_TAB);
    };

  const profileTab: NavItem = {
    label: t(ROUTE_LABELS[ROUTES.PROFILE]),
    path: ROUTES.PROFILE,
    testId: 'Profile',
  };

  const tabItemsClassName = isOpen ? `${styles.tabItemsContainer} ${styles.visible}` : styles.tabItemsContainer;

  useEffect(() => {
    close();
  }, [useMobileLayout]);

  const availableLanguages = supportedLocales[userLocale].availableLanguages;
  const showLanguageToggle = availableLanguages.length > 1;

  if (useMobileLayout) {
    return (
      <>
        <div className={styles.offsetElement} />
        <nav className={styles.navContainer} data-cy={testId}>
          <div className={styles.navItemContainer}>
            <HamburgerMenuButton isOpen={isOpen} onClick={() => (isOpen ? close() : open())} />
            <ViasatLogo
              logoUrl={`${urlPrefix}${ROUTES.HOME}`}
              onClick={createNavItemClickHandler(ROUTES.HOME)}
              showLanguageToggle={showLanguageToggle}
            />
            <div className={tabItemsClassName}>
              <CommonTabs items={tabItems} createNavItemClickHandler={createNavItemClickHandler} />
              <TabItem
                key={profileTab.label}
                {...profileTab}
                path={`${urlPrefix}${profileTab.path}`}
                onClick={createNavItemClickHandler(profileTab.path)}
                showIcon={false}
              />
              <MobileBottomNav />
            </div>
            {showLanguageToggle && <LanguageToggle />}
          </div>
        </nav>
      </>
    );
  }

  return (
    <>
      <div className={styles.offsetElement} />
      <nav className={styles.navContainer} data-cy={testId}>
        <div className={styles.navItemContainer}>
          <div className={styles.logoAndTabsContainer}>
            <div className={styles.logoContainer}>
              <ViasatLogo logoUrl={`${urlPrefix}${ROUTES.HOME}`} onClick={createNavItemClickHandler(ROUTES.HOME)} />
            </div>
            <div className={styles.dividerContainer}>
              <div className={styles.divider} />
            </div>
            <div className={styles.tabItemsContainer}>
              <CommonTabs items={tabItems} createNavItemClickHandler={createNavItemClickHandler} />
            </div>
          </div>

          <div className={styles.languageToggleAndProfileContainer}>
            {showLanguageToggle && (
              <>
                <LanguageToggle />

                <div className={styles.dividerContainer}>
                  <div className={styles.divider} />
                </div>
              </>
            )}
            <ProfileButton navigationTab={profileTab} createNavItemClickHandler={createNavItemClickHandler} />
          </div>
        </div>
      </nav>
    </>
  );
};

export default HeaderNavigation;
